import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    useTheme,
    useMediaQuery,
    IconButton,
} from '@mui/material';
import {
    IconHistory,
    IconCurrencyRupee,
    IconClock,
    IconArrowLeft,
    IconCircleCheck,
    IconCircleX,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import { url } from '../../ConstUrl/Url';
import { colors, fonts } from '../../ColorFont/color';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../context/UserContext';

const PurchaseHistory = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { t } = useTranslation(['dashboard', 'common']);
    const { userData } = useUser();
    
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState([]);

    useEffect(() => {
        if (!userData?._id) {
            navigate('/home');
            return;
        }
        fetchHistory();
    }, []);

    const fetchHistory = async () => {
        try {
            setLoading(true);
            const isBuyer = userData?.role === 'Buyer' || userData?.role === 'Supplier';
            const endpoint = isBuyer 
                ? `${url}/api/users/${userData._id}/plan-purchase-history` 
                : `${url}/api/users/${userData._id}/seller-plan-purchase-history`;
            
            const response = await axios.get(endpoint);
            // The backend returns different keys sometimes, let's normalize
            const rawData = response.data.purchasedSellerPlans || response.data || [];
            // Sort by date descending
            const sortedData = (rawData).sort((a, b) => dayjs(b.purchaseDate).diff(dayjs(a.purchaseDate)));
            setHistory(sortedData);
        } catch (error) {
            console.error("Error fetching purchase history:", error);
            setHistory([]); 
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: isMobile ? '16px' : '24px',
            minHeight: '100%',
        }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ color: colors.primary_dark }}>
                    <IconArrowLeft size={24} />
                </IconButton>
                <Box>
                    <Typography sx={{
                        fontFamily: fonts.poppins,
                        fontWeight: 800,
                        fontSize: isMobile ? '24px' : '30px',
                        color: colors.primary_dark,
                    }}>
                        {t("purchaseHistoryTitle")}
                    </Typography>
                    <Typography sx={{
                        fontFamily: fonts.poppins,
                        fontSize: '14px',
                        color: colors.text,
                        opacity: 0.8
                    }}>
                        {t("trackTransactionsSub")}
                    </Typography>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ width: '100%' }}>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 2, borderRadius: 2 }} />
                    ))}
                </Box>
            ) : history.length === 0 ? (
                <Paper sx={{
                    p: 6,
                    textAlign: 'center',
                    borderRadius: 4,
                    border: `1px dashed ${colors.primary_dark}80`,
                    bgcolor: colors.primary_rgba_4,
                }}>
                    <IconHistory size={64} color={colors.primary_dark} opacity={0.5} stroke={1} />
                    <Typography sx={{ fontFamily: fonts.poppins, fontWeight: 700, fontSize: '20px', mt: 2, color: colors.dark }}>
                        {t("noPurchasesYet")}
                    </Typography>
                    <Typography sx={{ fontFamily: fonts.poppins, fontSize: '14px', color: colors.text, mt: 1 }}>
                        {t("emptyPurchasesSub")}
                    </Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{
                    borderRadius: 4,
                    border: `1px solid ${colors.border || '#E3E8E3'}`,
                    overflow: 'hidden'
                }}>
                    <Table>
                        <TableHead sx={{ bgcolor: colors.primary_rgba_5 }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, fontFamily: fonts.poppins }}>{t('dashboard:planDetails')}</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontFamily: fonts.poppins }}>{t('dashboard:amountLabel')}</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontFamily: fonts.poppins }}>{t('dashboard:resources')}</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontFamily: fonts.poppins }}>{t('dashboard:status')}</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontFamily: fonts.poppins }}>{t('dashboard:dateTime')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.map((record, index) => (
                                <TableRow key={index} sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.01)' } }}>
                                    <TableCell>
                                        <Typography sx={{ fontWeight: 700, fontSize: '15px', fontFamily: fonts.poppins }}>{record.name || t('dashboard:unknownPlan')}</Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#637381', fontFamily: fonts.poppins }}>ID: {record.planId?.slice(-8).toUpperCase() || "N/A"}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <IconCurrencyRupee size={16} color={colors.primary_dark} />
                                            <Typography sx={{ fontWeight: 800, fontSize: '16px', color: colors.primary_dark, fontFamily: fonts.poppins }}>
                                                {record.discountedPrice || record.amount || 0}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconClock size={16} color="#637381" />
                                            <Typography sx={{ fontSize: '14px', fontWeight: 600, fontFamily: fonts.poppins }}>
                                                {record.contacts ? `${record.contacts} ${t('common:contactsLabel').replace(':', '').trim()}` : record.sponseredHrs ? `${record.sponseredHrs} ${t('common:hours')}` : "N/A"}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: '20px',
                                            bgcolor: colors.primary_rgba_10,
                                            color: colors.primary_dark
                                        }}>
                                            <IconCircleCheck size={16} />
                                            <Typography sx={{ fontSize: '12px', fontWeight: 700, fontFamily: fonts.poppins }}>{t('dashboard:successStatus')}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500, fontFamily: fonts.poppins }}>
                                            {dayjs(record.purchaseDate).format('DD MMM YYYY')}
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#637381', fontFamily: fonts.poppins }}>
                                            {dayjs(record.purchaseDate).format('hh:mm A')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Support Hint */}
            <Typography sx={{
                textAlign: 'center',
                mt: 6,
                fontSize: '13px',
                fontFamily: fonts.poppins,
                color: colors.text,
                opacity: 0.7
            }}>
                {t('dashboard:paymentSupportNote')} <span style={{ fontWeight: 600 }}>krushisetuofficial@gmail.com</span>
            </Typography>
        </Box>
    );
};

export default PurchaseHistory;
